// Office defects page. Lists defects raised against lifts (from completed job
// sheets or logged by the office) with a severity badge, age and status, and a
// one-click Resolve. Filter by open / resolved / all. Plain English, zero jargon.
import { Fragment, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import type { DefectFixState, DefectListItem, DefectOrigin, DefectStatusFilter, DefectRiddorFlag, RiddorFlagStatus } from "../../lib/api";
import {
  DEFECT_FIX_STATE_LABEL,
  DEFECT_FIX_STATE_OPTIONS,
  DEFECT_FIX_STATE_TONE,
  DEFECT_ORIGIN_LABEL,
  DEFECT_ORIGIN_OPTIONS,
  isDefectFixStateFilter,
  DEFECT_FIX_STATE_KIND,
  isDefectOrigin,
} from "../../lib/defectFixState";
import { useAuth } from "../../lib/auth";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { StatusPill } from "../../components/StatusPill";
import { statePill } from "../../lib/statusRegistry";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { useSiteFilter, SiteFilterChip, useJobFilter, JobFilterNotice } from "./customerFilter";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

// Session SC2C: the list is 50 at a time and the SERVER filters, searches and sorts it (GET /api/defects, defectListQuery.ts); the sort
// columns are lift, site, severity, description, age and status. The Action column is not sortable.
const DEFECT_PAGE_SIZE = 50;

// Category filter (the compliance dashboard links here pre-filtered): A = immediate ("Category A"),
// B = advisory ("Category B"). Defaults to all.
type CategoryFilter = "all" | "A" | "B";
const CATEGORY_FILTERS: { value: CategoryFilter; label: string }[] = [
  { value: "all", label: "All severities" },
  { value: "A", label: "Immediate (Cat A)" },
  { value: "B", label: "Advisory (Cat B)" },
];

function isStatusFilter(v: string | null): v is DefectStatusFilter {
  return v === "open" || v === "resolved" || v === "all";
}

// Shared filter-pill styling (status, category, RIDDOR toggle all use it). The active colour is
// green for the normal filters and danger red for the RIDDOR toggle.
function filterButtonClass(isActive: boolean, activeColor: "green" | "danger" = "green"): string {
  const active = activeColor === "danger" ? "border-ele-danger bg-ele-inset text-ele-danger" : "border-ele-green bg-ele-inset text-ele-green";
  return `rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    isActive ? active : "border-ele-purple-accent text-ele-body hover:text-ele-green"
  }`;
}

const RIDDOR_STATUS_LABEL: Record<RiddorFlagStatus, string> = {
  open: "Open",
  reviewed: "Reviewed",
  dismissed: "Dismissed",
  reported_by_director: "Reported by Director",
};

// The advisory banner for a defect's RIDDOR flag. An open flag is shown prominently with the
// criterion + reporting-timescale guidance and the office actions; a reviewed/dismissed/
// reported flag shows a subtle status line with a reopen control. The platform never marks a
// flag reported automatically -- "Mark reported by Director" records a human decision only.
export function RiddorBanner({ flag, onUpdated }: { flag: DefectRiddorFlag; onUpdated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function setStatus(status: RiddorFlagStatus) {
    setBusy(true);
    setErr(null);
    try {
      await api.riddor.updateFlag(flag.id, { status, note: note.trim() || null });
      onUpdated();
    } catch {
      setErr("Could not update the RIDDOR flag. Please try again.");
      setBusy(false);
    }
  }

  if (flag.status !== "open") {
    return (
      <div className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-xs text-ele-body">
        <span className="font-menu uppercase tracking-wide text-ele-blue">RIDDOR advisory: {RIDDOR_STATUS_LABEL[flag.status]}</span>
        <span className="ml-2">{flag.matchedCriterion}</span>
        {flag.reviewedByNote ? <span className="ml-2 opacity-80">Note: {flag.reviewedByNote}</span> : null}
        <button type="button" onClick={() => setStatus("open")} disabled={busy} className="ml-3 text-ele-blue hover:underline disabled:opacity-50">
          Reopen
        </button>
        {err && <p role="alert" className="mt-1 text-ele-danger">{err}</p>}
      </div>
    );
  }

  return (
    <div role="alert" className="rounded-ele border border-ele-danger bg-red-500/10 p-3 text-sm">
      <p className="font-heading uppercase tracking-wide text-ele-danger">This may be RIDDOR-reportable</p>
      <p className="mt-1 text-ele-body">{flag.matchedCriterion}</p>
      <p className="mt-1 text-ele-body">Reporting guidance: {flag.timescaleGuidance}</p>
      <p className="mt-1 text-xs text-ele-body opacity-80">
        This is guidance, not legal advice. Review and confirm whether a report to the HSE is required.
      </p>
      <div className="mt-2">
        <label className="mb-1 block text-xs text-ele-body" htmlFor={`riddor-note-${flag.id}`}>Note (optional)</label>
        <input
          id={`riddor-note-${flag.id}`}
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green"
          placeholder="e.g. Reviewed with the Director; not reportable"
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => setStatus("reviewed")} disabled={busy} className="rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue hover:bg-ele-blue/10 disabled:opacity-50">
          Mark reviewed
        </button>
        <button type="button" onClick={() => setStatus("dismissed")} disabled={busy} className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50">
          Dismiss
        </button>
        <Button variant="secondary" size="sm" onClick={() => setStatus("reported_by_director")} disabled={busy}>
          Mark reported by Director
        </Button>
      </div>
      {err && <p role="alert" className="mt-2 text-ele-danger">{err}</p>}
    </div>
  );
}

const FILTERS: { value: DefectStatusFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

// Session AHB: the card's one deliberate truncation. Three ASCII dots, matching the platform's
// existing truncation helpers (QuotesPage's own 60-character site line, ProjectDetailPage and
// lib/scopeSummary all use "..."), never a Unicode ellipsis and never a double hyphen.
function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max).trimEnd()}...`;
}

// Session BAT. The severity pill in the table is a short label ("Immediate A", "Advisory B") so it never
// clips; the full wording is the hover title and the screen reader text (the visible short word is hidden
// from assistive tech so it is not read twice). Tone and solid fill match the registry's defectSeverity.
const SEVERITY_SHORT: Record<DefectListItem["severity"], { word: string; full: string; tone: "red" | "amber" }> = {
  immediate: { word: "Immediate A", full: "Immediate (Cat A)", tone: "red" },
  advisory: { word: "Advisory B", full: "Advisory (Cat B)", tone: "amber" },
};

// "Today" for a defect raised today, otherwise "N days" (Session BAT).
function ageLabel(days: number): string {
  return days <= 0 ? "Today" : `${days} ${days === 1 ? "day" : "days"}`;
}

// Where a defect came from, shown only when the payload says (never guessed from the description).
// Session SC7A: the origin of a defect (the server's derived answer; an older payload that only says source loler still reads).
function originOf(d: DefectListItem): DefectOrigin | null {
  return d.origin ?? (d.source === "loler" ? "loler" : null);
}

// The fault's own status (Open or Resolved) ALWAYS shows, and while it is open the progress of the fix shows beside it, as a pill that
// carries the words. A finished repair job leaves the fault open ("Job done: confirm the fault is gone"); only the office resolving it
// reads Resolved. An older payload with no fixState shows the status pill alone, as it always did.
function FixStateLabel({ d, className }: { d: DefectListItem; className?: string }) {
  const status = <StatusPill domain="defectStatus" status={d.status} className={className} />;
  if (!d.fixState || d.status === "resolved") return status;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {status}
      {DEFECT_FIX_STATE_KIND[d.fixState] ? (
        <StatusPill state={statePill(DEFECT_FIX_STATE_KIND[d.fixState]!)} className={className} />
      ) : (
        <StatusPill word={DEFECT_FIX_STATE_LABEL[d.fixState]} tone={DEFECT_FIX_STATE_TONE[d.fixState]} className={className} />
      )}
    </span>
  );
}

// A quiet marker chip under a description (replaces the shouting uppercase lines).
const QUIET_CHIP = "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] leading-4";

export function DefectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // The compliance dashboard links here pre-filtered (Session AF, everything links): ?status=open,
  // ?category=A|B (Cat A = immediate, Cat B = advisory), ?riddor=open (only defects with an open
  // RIDDOR advisory flag). Read once on mount; subsequent in-page filter changes are local state.
  const [searchParams] = useSearchParams();
  // Session ADI: the site detail page links here with ?siteId= to show only that site's defects.
  const { siteId, clear: clearSite } = useSiteFilter();
  // Session SC9A: a Follow-on works pill links here with ?jobId= to show only the faults raised from that visit.
  const { jobId, clear: clearJob } = useJobFilter();
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [filter, setFilter] = useState<DefectStatusFilter>(() => {
    const s = searchParams.get("status");
    // An explicit ?status= wins (the compliance dashboard deep-links open/Cat A/etc.); otherwise a
    // site-scoped view defaults to "all" so the site's full defect history shows, not just open ones.
    return isStatusFilter(s) ? s : siteId ? "all" : "open";
  });
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(() => {
    const c = searchParams.get("category");
    return c === "A" || c === "B" ? c : "all";
  });
  const [riddorOnly, setRiddorOnly] = useState<boolean>(() => searchParams.get("riddor") === "open");
  // Session SC7A: where it came from, and how far the fix has got. Read once from the address like the filters above.
  const [originFilter, setOriginFilter] = useState<DefectOrigin | "all">(() => {
    const o = searchParams.get("origin");
    return isDefectOrigin(o) ? o : "all";
  });
  const [stateFilter, setStateFilter] = useState<DefectFixState | "to_do" | "all">(() => {
    const st = searchParams.get("state");
    return isDefectFixStateFilter(st) ? st : "all";
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  // Resolve/delete failures surface inline (Session AEH, FIX-1 A1: no native window.alert).
  const [actionError, setActionError] = useState<string | null>(null);

  // AI triage (advisory): one expandable row at a time. triageOverrides holds the values
  // refreshed by a regenerate so the panel updates without a full reload.
  const [triageOpen, setTriageOpen] = useState<string | null>(null);
  const [triageOverrides, setTriageOverrides] = useState<Map<string, string | null>>(new Map());
  const [triageBusy, setTriageBusy] = useState<string | null>(null);
  const [triageError, setTriageError] = useState<string | null>(null);

  function triageFor(d: DefectListItem): string | null {
    return triageOverrides.has(d.id) ? triageOverrides.get(d.id)! : d.aiTriage;
  }

  async function regenerateTriage(id: string) {
    setTriageBusy(id);
    setTriageError(null);
    try {
      const res = await api.defects.regenerateTriage(id);
      setTriageOverrides((m) => new Map(m).set(id, res.aiTriage));
      if (!res.regenerated) {
        setTriageError("No new triage was generated. The AI may not be configured. Any existing triage is kept.");
      }
    } catch {
      setTriageError("Could not regenerate the triage. Please try again.");
    } finally {
      setTriageBusy(null);
    }
  }

  const sort = useSortableColumn("defects");
  const { search, setSearch } = useListSearch();
  // The search the server is asked for is the one in the address bar (the toolbar writes it there after a short pause).
  const urlSearch = (searchParams.get("q") ?? "").trim();
  const queryKey = JSON.stringify({ filter, categoryFilter, riddorOnly, originFilter, stateFilter, siteId, jobId, urlSearch, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.defects.listPage({
      status: filter,
      ...(categoryFilter !== "all" ? { category: categoryFilter } : {}),
      ...(riddorOnly ? { riddor: true } : {}),
      ...(originFilter !== "all" ? { origin: originFilter } : {}),
      ...(stateFilter !== "all" ? { state: stateFilter } : {}),
      ...(siteId ? { siteId } : {}),
      ...(jobId ? { jobId } : {}),
      ...(urlSearch ? { q: urlSearch } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
      limit: DEFECT_PAGE_SIZE,
      offset: at,
    }),
  );
  const loading = page.loading;
  const error = page.error;
  const sorted: DefectListItem[] = page.data?.items ?? [];
  const total = page.data?.total ?? 0;

  async function resolve(id: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await api.defects.resolve(id, "resolved");
      await page.reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message || err.code : "Could not resolve the defect.");
    } finally {
      setBusyId(null);
    }
  }

  // Delete a defect logged or auto-raised in error (admin only). Shown only for an untouched
  // defect (no drafted notification, no remedial job, no Director-recorded RIDDOR); the server
  // enforces the full guard and returns a plain-English 409 if it has since been actioned.
  function canDelete(d: DefectListItem): boolean {
    return isAdmin && !d.notificationDrafted && !d.remedialJobCreated && !(d.riddorFlag != null && d.riddorFlag.status === "reported_by_director");
  }

  async function del(id: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await api.defects.remove(id);
      await page.reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message || err.code : "Could not delete the defect.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="To fix"
        subtitle="Every fault waiting to be fixed, wherever it was found: a service visit, a call-out, a LOLER report or an insurer. Each row says where it came from and what has happened to it so far. Immediate (Category A) defects require the lift to be taken out of service until resolved."
        actions={
          // Session GAP9: the CSV of this list as it is showing (the same filters, search and sort), in the More actions menu.
          <ExportCsvMenu
            list="defects"
            params={{
              status: filter,
              ...(categoryFilter !== "all" ? { category: categoryFilter } : {}),
              ...(riddorOnly ? { riddor: "open" } : {}),
              ...(originFilter !== "all" ? { origin: originFilter } : {}),
              ...(stateFilter !== "all" ? { state: stateFilter } : {}),
              ...(siteId ? { siteId } : {}),
              ...(jobId ? { jobId } : {}),
              ...(urlSearch ? { q: urlSearch } : {}),
              ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
            }}
          />
        }
      />

      {/* The shared list toolbar (design system Session 4): a search over lift, site, description and
          severity, kept in the URL as ?q= beside the existing status, category, riddor and siteId keys. */}
      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Lift, site or description"
        searchLabel="Search defects"
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)} className={filterButtonClass(filter === f.value)}>
            {f.label}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-ele-purple-accent" aria-hidden />
        {CATEGORY_FILTERS.map((c) => (
          <button key={c.value} onClick={() => setCategoryFilter(c.value)} className={filterButtonClass(categoryFilter === c.value)}>
            {c.label}
          </button>
        ))}
        <button onClick={() => setRiddorOnly((v) => !v)} aria-pressed={riddorOnly} className={filterButtonClass(riddorOnly, "danger")}>
          RIDDOR flagged
        </button>
        <span className="mx-1 h-5 w-px bg-ele-purple-accent max-sm:hidden" aria-hidden />
        <label className="flex items-center gap-2 text-xs text-ele-body">
          <span className="sr-only sm:not-sr-only">Where from</span>
          <select
            aria-label="Where from"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value as DefectOrigin | "all")}
            className="rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1.5 text-ele-strong max-sm:min-h-11"
          >
            <option value="all">Any source</option>
            {DEFECT_ORIGIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-ele-body">
          <span className="sr-only sm:not-sr-only">Progress</span>
          <select
            aria-label="Progress"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as DefectFixState | "to_do" | "all")}
            className="rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1.5 text-ele-strong max-sm:min-h-11"
          >
            <option value="all">Any progress</option>
            <option value="to_do">Not done yet</option>
            {DEFECT_FIX_STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {siteId && (
        <div className="mb-4">
          <SiteFilterChip noun="defects" onClear={clearSite} />
        </div>
      )}
      {jobId && (
        <div className="mb-4">
          <JobFilterNotice noun="faults" onClear={clearJob} />
        </div>
      )}

      {(() => {
        // Session SC2C: the server names the sites with a drafted notification waiting (it used to be worked out from the whole loaded list).
        const sites = page.data?.readySites ?? [];
        if (sites.length === 0) return null;
        return (
          <div role="status" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface p-4">
            <p className="font-heading text-sm text-ele-danger">
              Draft notification ready for {sites.join(", ")}: review and send.
            </p>
            <p className="mt-1 text-xs text-ele-body">
              An out-of-service client notification has been drafted to the Gmail drafts folder. Open Gmail to review and send it.
            </p>
          </div>
        );
      })()}

      {actionError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {actionError}
        </p>
      )}

      {loading ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the defects. Please try again in a moment.
        </p>
      ) : sorted.length === 0 ? (
        urlSearch ? (
          <EmptyState role="status" message={`No defects match "${urlSearch}".`} />
        ) : (
          <EmptyState
            role={categoryFilter !== "all" || riddorOnly || originFilter !== "all" || stateFilter !== "all" ? "status" : undefined}
            message={
              categoryFilter !== "all" || riddorOnly || originFilter !== "all" || stateFilter !== "all"
                ? "No defects match this filter."
                : filter === "open"
                  ? "No open defects. Everything is clear."
                  : "No defects to show."
            }
          />
        )
      ) : mobileCards ? (
        /* Session AHB. The mobile column set here was the least useful of the ten: it kept
           Lift, Severity and Age and dropped the DESCRIPTION and the STATUS, so a phone could
           see that something was wrong and how old it was but not what it was or whether it
           had been dealt with. The card carries all five. The description is the one field
           this session truncates on purpose (a list is for scanning; the full text is on the
           detail page the card taps through to). */
        <MobileCardList label="Defects">
          {sorted.map((d) => (
            <MobileListCard
              key={d.id}
              to={`/office/defects/${d.id}`}
              primary={
                <>
                  <span className="font-heading">{d.eleNumber}</span>
                  <span className="text-ele-body"> | {d.siteName}</span>
                </>
              }
              secondary={truncate(d.description, 60)}
              meta={`${originOf(d) ? `${DEFECT_ORIGIN_LABEL[originOf(d)!]} | ` : ""}${d.ageDays <= 0 ? "Raised today" : `Raised ${d.ageDays} ${d.ageDays === 1 ? "day" : "days"} ago`}`}
              right={
                <>
                  <StatusPill domain="defectSeverity" status={d.severity} />
                  <FixStateLabel d={d} />
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "10%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "28%" }} />
              <col style={{ width: "8%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "19%" }} />
              {/* Mobile-only disclosure chevron (Session ACW Loop 3): the row navigates to the
                  defect detail page below md, where the Action column (Triage/Resolve/Delete)
                  is tucked away for space; those controls remain available on the detail page. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Lift" colKey="lift" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Severity" colKey="severity" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Description" colKey="description" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Age" colKey="age" sort={sort} onToggle={sort.toggle} align="right" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="cell-actions max-md:hidden px-3 py-2 text-right">Action</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <Fragment key={d.id}>
                  <tr
                    className="group cursor-pointer border-t border-ele-purple-accent"
                    onClick={(e) => {
                      // A tap anywhere in the row opens the defect detail page below md, EXCEPT
                      // on an interactive control (the Lift link, Triage/Resolve/Delete buttons)
                      // which keep their own behaviour (Loop 3, row tap pattern).
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      navigate(`/office/defects/${d.id}`);
                    }}
                  >
                    <td className="px-3 py-2 align-top font-heading text-ele-strong">
                      <Link to={`/office/defects/${d.id}`} className="hover:text-ele-green hover:underline">{d.eleNumber}</Link>
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {/* Forward click-through to the site (Session ADI). The row's onClick ignores
                          clicks that land on an <a>, so this link navigates to the site rather than
                          opening the defect detail. */}
                      {d.siteId ? (
                        <Link to={`/office/sites/${encodeURIComponent(d.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                          {d.siteName}
                        </Link>
                      ) : (
                        d.siteName
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span title={SEVERITY_SHORT[d.severity].full} className="inline-block whitespace-nowrap">
                        <span aria-hidden>
                          <StatusPill word={SEVERITY_SHORT[d.severity].word} tone={SEVERITY_SHORT[d.severity].tone} variant="solid" className="whitespace-nowrap" />
                        </span>
                        <span className="sr-only">{SEVERITY_SHORT[d.severity].full}</span>
                      </span>
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {originOf(d) && (
                        <span data-testid="defect-source" data-source={originOf(d)!} title={`Where from: ${DEFECT_ORIGIN_LABEL[originOf(d)!]}`} className="mb-1 inline-block">
                          <StatusPill word={DEFECT_ORIGIN_LABEL[originOf(d)!]} tone={originOf(d) === "loler" ? "loler" : "grey"} />
                        </span>
                      )}
                      {/* Two lines, then an ellipsis; the full text is the hover title. The markers
                          below stay on their own wrapping line so a long description never clips
                          them (Session AJ Item 4 review, Session BAT). */}
                      <div className="line-clamp-2 whitespace-normal break-words" title={d.description}>{d.description}</div>
                      {(d.notificationDrafted || d.remedialJobCreated || d.riddorFlag || d.linkedQuote) && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal">
                          {d.notificationDrafted && <span className={`${QUIET_CHIP} border-ele-danger text-ele-danger`}>Notification drafted</span>}
                          {d.remedialJobCreated && (
                            <span className={`${QUIET_CHIP} border-ele-purple-accent text-ele-body`} data-testid="defect-remedial-chip">
                              Remedial job
                            </span>
                          )}
                          {d.linkedQuote && (
                            <Link
                              to={`/office/quotes/${encodeURIComponent(d.linkedQuote.id)}`}
                              className="text-[10px] uppercase tracking-wide text-ele-blue underline-offset-2 hover:underline"
                              data-testid="defect-linked-quote"
                            >
                              Quote {d.linkedQuote.reference}: {d.linkedQuote.status.replace(/_/g, " ")}
                              {(d.linkedQuoteCount ?? 0) > 1 ? ` (${d.linkedQuoteCount} quotes)` : ""}
                            </Link>
                          )}
                          {d.riddorFlag && (
                            <span className={`text-[10px] uppercase tracking-wide ${d.riddorFlag.status === "open" ? "text-ele-danger" : "text-ele-blue"}`}>
                              RIDDOR: {d.riddorFlag.status === "open" ? "review" : RIDDOR_STATUS_LABEL[d.riddorFlag.status].toLowerCase()}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right align-top tabular-nums">{ageLabel(d.ageDays)}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      <FixStateLabel d={d} className="whitespace-nowrap" />
                    </td>
                    <td className="cell-actions max-md:hidden px-3 py-1 text-right align-top">
                      <div className="flex flex-wrap items-center justify-end gap-x-1">
                        <button
                          type="button"
                          onClick={() => setTriageOpen((cur) => (cur === d.id ? null : d.id))}
                          aria-expanded={triageOpen === d.id}
                          className="inline-flex min-h-11 items-center px-2 text-ele-blue hover:underline"
                        >
                          {triageOpen === d.id ? "Hide triage" : "Triage"}
                        </button>
                        {d.status === "open" ? (
                          <button
                            onClick={() => resolve(d.id)}
                            disabled={busyId === d.id}
                            className="inline-flex min-h-11 items-center px-2 text-ele-green hover:underline disabled:opacity-50"
                          >
                            {busyId === d.id ? "Resolving..." : "Resolve"}
                          </button>
                        ) : (
                          <span className="inline-flex min-h-11 items-center px-2 text-ele-body opacity-60">Resolved</span>
                        )}
                        {canDelete(d) && (
                          <ConfirmButton
                            onConfirm={() => del(d.id)}
                            label="Delete"
                            busy={busyId === d.id}
                            className="inline-flex min-h-11 items-center px-2 text-ele-danger hover:underline disabled:opacity-50"
                          />
                        )}
                      </div>
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                  {d.riddorFlag && (
                    <tr className="border-t border-ele-purple-accent bg-ele-surface-soft">
                      <td colSpan={8} className="cell-wrap px-3 py-3">
                        <RiddorBanner flag={d.riddorFlag} onUpdated={() => void page.reload()} />
                      </td>
                    </tr>
                  )}
                  {triageOpen === d.id && (
                    <tr className="border-t border-ele-purple-accent bg-ele-surface-soft">
                      <td colSpan={8} className="cell-wrap px-3 py-3">
                        <h3 className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-blue">AI triage (advisory)</h3>
                        <p className="mb-2 text-[11px] text-ele-body opacity-70">
                          AI-generated and advisory only. Not a professional engineering assessment.
                        </p>
                        {triageFor(d) ? (
                          <div className="space-y-1 text-sm text-ele-body">
                            {triageFor(d)!
                              .split("\n")
                              .map((line) => line.trim())
                              .filter(Boolean)
                              .map((line, i) => (
                                <p key={i}>{line}</p>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-ele-body opacity-70">No triage yet.</p>
                        )}
                        {triageError && (
                          <p role="alert" className="mt-2 text-sm text-ele-danger">
                            {triageError}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => regenerateTriage(d.id)}
                          disabled={triageBusy === d.id}
                          className="mt-2 rounded-ele border border-ele-purple px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green disabled:opacity-50"
                        >
                          {triageBusy === d.id ? "Regenerating..." : "Regenerate triage"}
                        </button>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Session SC2C: 50 at a time; the total is the server's count of every defect matching the filters and search. */}
      {!loading && !error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={sorted.length} pageSize={DEFECT_PAGE_SIZE} onOffset={page.setOffset} noun={["defect", "defects"]} />
        </div>
      )}
    </div>
  );
}
